import { useAiAgentStore } from "@/stores/ai-agent";
import { ProgressEventData } from "@/hooks/services/mutations/ai-agent/use-demo-chat-stream-mutation";
import { Text } from "../ui/text";
import { cn } from "@/lib/utils";
import StatusTag from "../deep-research/status-tag";
import ToggleButton from "../deep-research/toggle-button";

interface PipelineProps {
  chatId: string;
}

const Pipeline = ({ chatId }: PipelineProps) => {
  const progressList = useAiAgentStore((state) => state.getProgressList(chatId));
  const setPipelineExpandState = useAiAgentStore((state) => state.setPipelineExpandState);
  const isStreamComplete = useAiAgentStore((state) => state.isStreamComplete(chatId));

  // Zustand Map에서 직접 읽어서 구독
  const expandState = useAiAgentStore((state) => {
    const stored = state.pipelineExpandStateMap.get(chatId);
    if (!stored) {
      return {
        isExpanded: false,
        isNoteExpanded: false,
        expandedPhases: new Set<string>(),
        expandedTasks: new Set<string>(),
      };
    }
    // Set이 객체로 변환되었을 수 있으므로 확실하게 Set으로 변환
    return {
      isExpanded: stored.isExpanded,
      isNoteExpanded: stored.isNoteExpanded,
      expandedPhases: stored.expandedPhases instanceof Set ? stored.expandedPhases : new Set(stored.expandedPhases ? Object.keys(stored.expandedPhases) : []),
      expandedTasks: stored.expandedTasks instanceof Set ? stored.expandedTasks : new Set(stored.expandedTasks ? Object.keys(stored.expandedTasks) : []),
    };
  });
  const { isExpanded, isNoteExpanded, expandedPhases, expandedTasks } = expandState;

  const setIsExpanded = (value: boolean) => {
    setPipelineExpandState(chatId, { isExpanded: value });
  };

  const setIsNoteExpanded = (value: boolean) => {
    setPipelineExpandState(chatId, { isNoteExpanded: value });
  };

  const setExpandedPhases = (value: Set<string>) => {
    setPipelineExpandState(chatId, { expandedPhases: value });
  };

  const setExpandedTasks = (value: Set<string>) => {
    setPipelineExpandState(chatId, { expandedTasks: value });
  };

  const { phaseMap, taskMap, logEvents } = progressList.reduce(
    (acc, item) => {
      if (item.type === "phase" && item.title) {
        const existing = acc.phaseMap.get(item.title);
        if (!existing || item.status === "completed") {
          acc.phaseMap.set(item.title, item);
        }
      } else if (item.type === "task" && item.taskId) {
        const existing = acc.taskMap.get(item.taskId);
        if (!existing || item.status === "completed") {
          acc.taskMap.set(item.taskId, item);
        }
      } else if (
        item.type === "log" ||
        item.type === "status" ||
        (item.type === "tool" && item.status === "completed")
      ) {
        acc.logEvents.push(item);
      }
      return acc;
    },
    {
      phaseMap: new Map<string, ProgressEventData>(),
      taskMap: new Map<string, ProgressEventData>(),
      logEvents: [] as ProgressEventData[],
    }
  );
  // 메시지 전체 완료(complete 이벤트) 시에만 "사고 과정" 완료 표시
  const displayStatus = isStreamComplete ? "complete" : "progress";

  // 접힌 상태용: (진행중 단계 / 총 단계) — 완료된 phase만 카운트
  const totalPhases = phaseMap.size;
  const phasesArray = Array.from(phaseMap.values());
  const completedPhaseCount = phasesArray.filter((p) => p.status === "completed").length;
  const currentPhaseStep = isStreamComplete ? totalPhases : completedPhaseCount;

  // 접힌 상태에 표시할 현재 단계 title (진행 중 or 마지막 phase)
  const currentPhaseTitle = phasesArray[Math.min(currentPhaseStep, totalPhases - 1)]?.title ?? "";

  if (progressList.length === 0) {
    return null;
  }

  return (
    <div className="w-fit pb-4">
      {isExpanded ? (
        <div className="w-fit  rounded-lg py-4 space-y-6 ">
          <div className="flex flex-col">
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2">
                <Text variant="title-lg" className="text-grey-20">
                  사고 과정
                </Text>
                <StatusTag status={displayStatus} />
              </div>
            </div>
            <Text variant="body-lg" className="text-grey-20">
              멀티 에이전트가 연구를 진행중입니다.
            </Text>
          </div>

          {/* Phase Section */}
          {phaseMap.size > 0 && (
            <div className="space-y-4">
              <Text variant="title-md" className="text-grey-20">
                1. 파이프라인 단계
              </Text>
              <div className="relative">
                <div className="space-y-0">
                  {Array.from(phaseMap.values()).map((phase, index) => {
                    const isCompleted = phase.status === "completed";
                    const isRunning = phase.status === "running";
                    const isPending = !isCompleted && !isRunning;
                    const phaseKey = phase.title || `phase-${index}`;
                    const isExpanded = expandedPhases.has(phaseKey);
                    const hasSummary = !!phase.summary;

                    const toggleExpand = () => {
                      const newExpanded = new Set(expandedPhases);
                      if (isExpanded) {
                        newExpanded.delete(phaseKey);
                      } else {
                        newExpanded.add(phaseKey);
                      }
                      setExpandedPhases(newExpanded);
                    };

                    const isLastItem =
                      index === Array.from(phaseMap.values()).length - 1;

                    return (
                      <div
                        key={index}
                        className={cn(
                          "flex gap-4 items-start pb-4 last:pb-0 transition-colors"
                        )}
                      >
                        <div className="flex flex-col items-center self-stretch">
                          <div className="w-[24px] h-[24px] flex items-center justify-center flex-shrink-0">
                            <div className="w-[10px] h-[10px] rounded-full bg-[#CCCCCC]"></div>
                          </div>
                          {!isLastItem && (
                            <div className="w-[2px] flex-1 bg-[#CCCCCC] min-h-[24px]"></div>
                          )}
                        </div>
                        {/* 컨텐츠 영역 */}
                        <div className="pt-0.5">
                          <div className="flex items-center gap-3 mb-2">
                            <Text
                              variant="body-lg"
                              className={cn(
                                isRunning && "text-primary-40",
                                isCompleted && "text-grey-20",
                                isPending && "text-grey-40",
                                "text-grey-20"
                              )}
                            >
                              {phase.title}
                            </Text>
                            <div className="flex items-center gap-3">
                              {/* 상태 배지 */}
                              {isCompleted ? (
                                <StatusTag status={"complete"} />
                              ) : isPending ? (
                                <StatusTag status={"progress"} />
                              ) : (
                                <StatusTag status={"progress"} />
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            {/* 설명 텍스트 (펼치기 시에만 표시) */}
                            {hasSummary && isExpanded && (
                              <Text
                                variant="body-light"
                                className="text-grey-40 leading-relaxed"
                              >
                                {phase.summary}
                              </Text>
                            )}
                            {/* 접기/펼치기 버튼 */}
                            {hasSummary && (
                              <ToggleButton
                                isExpanded={isExpanded}
                                toggleExpand={toggleExpand}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <hr className="border-grey-90 my-4 w-full" />

          {/* Task Section */}
          {taskMap.size > 0 && (
            <div className="space-y-4 w-fit">
              <Text variant="title-md" className="text-grey-20">
                2. 리서치 에이전트
              </Text>
              <div className="flex gap-[24px]">
                {Array.from(taskMap.values()).map((task, index) => {
                  const isCompleted = task.status === "completed";
                  const isRunning = task.status === "running";
                  const taskId = task.taskId || `task-${index}`;
                  const isTaskExpanded = expandedTasks.has(taskId);

                  const toggleTaskExpand = () => {
                    const newSet = new Set(expandedTasks);
                    if (isTaskExpanded) {
                      newSet.delete(taskId);
                    } else {
                      newSet.add(taskId);
                    }
                    setExpandedTasks(newSet);
                  };

                  return (
                    <div
                      key={taskId}
                      className={cn(
                        "w-[320px] flex-shrink-0 p-4",
                        isRunning && "bg-primary-5 border-primary-20",
                        isCompleted && "bg-grey-85 border-grey-80",
                        !isRunning &&
                          !isCompleted &&
                          "bg-grey-90 border-grey-80"
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-1 flex flex-col gap-[10px]">
                          <div className="flex items-center gap-[10px]">
                            <Text variant="body-lg" className="text-grey-20">
                              Agent {["A", "B", "C", "D"][index]}
                            </Text>
                            <div className="flex-shrink-0 mt-1">
                              {isCompleted ? (
                                <StatusTag status={"complete"} />
                              ) : isRunning ? (
                                <StatusTag status={"progress"} />
                              ) : (
                                <StatusTag status={"progress"} />
                              )}
                            </div>
                          </div>
                          {isTaskExpanded && (
                            <Text
                              variant="body-light"
                              className={cn(
                                isRunning && "text-primary-40",
                                isCompleted && "text-grey-20"
                              )}
                            >
                              {task.topic}
                            </Text>
                          )}
                          <ToggleButton
                            isExpanded={isTaskExpanded}
                            toggleExpand={toggleTaskExpand}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <hr className="border-grey-90 my-4 w-full" />
            </div>
          )}

          {/* Log Section */}
          {logEvents.length > 0 && (
            <div className="flex flex-col gap-[24px]">
              <div className="flex items-center gap-[6px]">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M5 18.3332C4.30556 18.3332 3.71528 18.0901 3.22917 17.604C2.74306 17.1179 2.5 16.5276 2.5 15.8332V13.3332H5V1.6665H17.5V15.8332C17.5 16.5276 17.2569 17.1179 16.7708 17.604C16.2847 18.0901 15.6944 18.3332 15 18.3332H5ZM15 16.6665C15.2361 16.6665 15.434 16.5866 15.5938 16.4269C15.7535 16.2672 15.8333 16.0693 15.8333 15.8332V3.33317H6.66667V13.3332H14.1667V15.8332C14.1667 16.0693 14.2465 16.2672 14.4062 16.4269C14.566 16.5866 14.7639 16.6665 15 16.6665ZM7.5 7.49984V5.83317H15V7.49984H7.5ZM7.5 9.99984V8.33317H15V9.99984H7.5ZM5 16.6665H12.5V14.9998H4.16667V15.8332C4.16667 16.0693 4.24653 16.2672 4.40625 16.4269C4.56597 16.5866 4.76389 16.6665 5 16.6665ZM5 16.6665H4.16667H12.5H5Z"
                    fill="#666666"
                  />
                </svg>
                <Text variant="title-md" className="text-grey-20">
                  사고 과정 노트
                </Text>
              </div>
              {isNoteExpanded && (
                <ul className="space-y-1 list-disc list-inside">
                  {logEvents.map((log, index) => (
                    <li key={index} className="text-xs text-grey-40">
                      <Text variant={"body-light"} className="text-grey-20">
                        {log.type === "tool" ? (
                          <span>
                            {log.toolName}:{" "}
                            {typeof log.output === "object"
                              ? JSON.stringify(log.output)
                              : log.output}
                          </span>
                        ) : (
                          <span>
                            {" "}
                            {typeof log.message === "object"
                              ? JSON.stringify(log.message)
                              : log.message}
                          </span>
                        )}
                      </Text>
                    </li>
                  ))}
                </ul>
              )}
              <ToggleButton
                isExpanded={isNoteExpanded}
                toggleExpand={() => setIsNoteExpanded(!isNoteExpanded)}
              />
            </div>
          )}
          <hr className="border-grey-90 my-4 w-full" />
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <div className="flex justify-center items-center w-12 h-12 flex-shrink-0">
            {!isStreamComplete && (
              <div className="w-8 h-8 border-4 border-primary-80 border-t-primary-40 rounded-full animate-spin" />
            )}
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-grey-20 text-base font-light">
              {totalPhases > 0 ? currentPhaseTitle : "사용자 요구사항을 분석중입니다.."}{" "}
              {totalPhases > 0 && (
                <span className="text-grey-40">{`(${currentPhaseStep} / ${totalPhases})`}</span>
              )}
            </span>
            <button
              onClick={() => setIsExpanded(true)}
              className="text-[#18988B] hover:text-[#027167] transition-colors text-sm font-medium text-left"
            >
              <Text
                variant="body-light"
                className="text-primary-20 hover:text-primary-40 inline-flex items-center gap-1"
              >
                자세히 보기
              </Text>
            </button>
          </div>
        </div>
      )}
      {isExpanded && (
        <button
          onClick={() => setIsExpanded(false)}
          className="text-[#18988B] hover:text-[#027167] transition-colors text-sm font-medium"
        >
          <Text
            variant="title-md"
            className="text-primary-20 hover:text-primary-40 flex items-center gap-1"
          >
            사고 과정 간단히 보기
          </Text>
        </button>
      )}
    </div>
  );
};

export default Pipeline;
